<!-- eslint-disable perfectionist/sort-imports -->
<!-- eslint-disable perfectionist/sort-modules -->
<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { onMounted, ref } from 'vue';
import { toast } from 'vue3-toastify';
import { useRouter } from 'vue-router';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import { campaignManagementService } from '~/services/campaign-management-api/campaign-management-api';

import IncentiveBannerUploader from '../components/IncentiveBannerUploader.vue';
import { mapCreateIncentivePayload } from '../mappers/incentiveMappers';
import { createEmptyIncentiveFormValues, type IncentiveFormValues, incentiveSchema } from '../schemas/incentiveSchema';
import { parseIncentiveApiError } from '../utils';

const router = useRouter();

const isSaving = ref(false);
const bannerUploaderRef = ref<InstanceType<typeof IncentiveBannerUploader> | null>(null);

const {
  defineField,
  errors,
  handleSubmit,
  setFieldError,
} = useForm<IncentiveFormValues>({
  initialValues: createEmptyIncentiveFormValues(),
  validationSchema: toTypedSchema(incentiveSchema),
});

const fieldOptions = {
  validateOnBlur: true,
  validateOnChange: false,
  validateOnInput: false,
  validateOnModelUpdate: false,
};

const [name, nameProps] = defineField('name', fieldOptions);
const [titlePtbr, titlePtbrProps] = defineField('title_ptbr', fieldOptions);
const [descriptionPtbr, descriptionPtbrProps] = defineField('description_ptbr', fieldOptions);
const [businessRules, businessRulesProps] = defineField('business_rules', fieldOptions);
const [triggerDescription, triggerDescriptionProps] = defineField('trigger_description', fieldOptions);
const [acceptanceCriteria, acceptanceCriteriaProps] = defineField('acceptance_criteria', fieldOptions);
const [bannerFileExternalId] = defineField('banner_file_external_id', fieldOptions);

const submitIncentive = handleSubmit(async (values) => {
  isSaving.value = true;

  try {
    let uploadedBannerId: null | string;

    try {
      uploadedBannerId = (await bannerUploaderRef.value?.upload()) ?? null;
    } catch {
      // The uploader already surfaced its own error toast — stop here without
      // creating the incentive with a stale/missing banner.
      return;
    }

    const payload = mapCreateIncentivePayload({ ...values, banner_file_external_id: uploadedBannerId });
    await campaignManagementService.createIncentive(payload);

    toast.success('Incentivo criado com sucesso!');
    router.push('/incentives');
  } catch (error) {
    const parsed = parseIncentiveApiError(error);

    if (parsed.kind === 'validation' && parsed.fieldErrors) {
      Object.entries(parsed.fieldErrors).forEach(([field, messages]) => {
        setFieldError(field as keyof IncentiveFormValues, messages[0]);
      });
    }

    toast.error(parsed.message);
  } finally {
    isSaving.value = false;
  }
}, () => {
  toast.error('Corrija os erros antes de salvar.');
});

function handleCancel() {
  router.push('/incentives');
}

onMounted(() => {
  document.title = 'Novo Incentivo';
});
</script>

<template>
  <DashboardLayout
    title="Revalida Instalação"
    description="Novo Incentivo"
    subtitle="Defina o incentivo para ser utilizado em campanhas de revalidação."
  >
    <template #actions>
      <div class="d-flex gap-2">
        <button
          type="button"
          class="btn btn-ghost-secondary"
          :disabled="isSaving"
          @click="handleCancel"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="isSaving"
          @click="submitIncentive"
        >
          <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" />
          Salvar incentivo
        </button>
      </div>
    </template>

    <div class="row row-cards">
      <div class="col-12">
        <div class="card">
          <div class="fs-section">
            <div class="fs-header">
              <div class="fs-num">1</div>
              <h4 class="fs-title">Identificação</h4>
            </div>
            <p class="fs-sub">Como este incentivo aparece na listagem e para o parceiro.</p>

            <div class="row g-3">
              <div class="col-12">
                <label class="fs-label" for="inc-name">
                  Nome <span class="fs-req">*</span>
                </label>
                <input
                  id="inc-name"
                  v-model="name"
                  v-bind="nameProps"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.name }"
                  placeholder="Ex.: Desconto na taxa de revalidação"
                />
                <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
                <small v-else class="fs-hint">Nome interno usado para identificar o incentivo nas campanhas.</small>
              </div>

              <div class="col-12">
                <label class="fs-label" for="inc-title">
                  Título <span class="fs-req">*</span>
                </label>
                <input
                  id="inc-title"
                  v-model="titlePtbr"
                  v-bind="titlePtbrProps"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.title_ptbr }"
                  placeholder="Ex.: 5% de desconto na revalidação"
                />
                <div v-if="errors.title_ptbr" class="invalid-feedback">{{ errors.title_ptbr }}</div>
                <small v-else class="fs-hint">Título exibido para o parceiro.</small>
              </div>

              <div class="col-12">
                <label class="fs-label" for="inc-description">
                  Descrição <span class="fs-req">*</span>
                </label>
                <textarea
                  id="inc-description"
                  v-model="descriptionPtbr"
                  v-bind="descriptionPtbrProps"
                  class="form-control"
                  :class="{ 'is-invalid': errors.description_ptbr }"
                  rows="3"
                  placeholder="Descreva o incentivo em poucas frases."
                />
                <div v-if="errors.description_ptbr" class="invalid-feedback">
                  {{ errors.description_ptbr }}
                </div>
                <small v-else class="fs-hint">Aparece na listagem de incentivos.</small>
              </div>
            </div>
          </div>

          <div class="fs-section">
            <div class="fs-header">
              <div class="fs-num">2</div>
              <h4 class="fs-title">Regras e critérios</h4>
            </div>
            <p class="fs-sub">Detalhes usados para configurar o gatilho e validar a elegibilidade.</p>

            <div class="row g-3">
              <div class="col-12">
                <label class="fs-label" for="inc-business-rules">Regras de negócio</label>
                <textarea
                  id="inc-business-rules"
                  v-model="businessRules"
                  v-bind="businessRulesProps"
                  class="form-control"
                  rows="3"
                  placeholder="Como o incentivo é calculado e aplicado."
                />
                <small class="fs-hint">
                  Opcional. Detalhe as regras para quem for configurar ou auditar o incentivo.
                </small>
              </div>

              <div class="col-12">
                <label class="fs-label" for="inc-trigger">Descrição do gatilho</label>
                <textarea
                  id="inc-trigger"
                  v-model="triggerDescription"
                  v-bind="triggerDescriptionProps"
                  class="form-control"
                  rows="3"
                  placeholder="O que dispara a concessão do incentivo."
                />
                <small class="fs-hint">Opcional. Explique o evento que aciona o incentivo.</small>
              </div>

              <div class="col-12">
                <label class="fs-label" for="inc-acceptance">Critérios de aceite</label>
                <textarea
                  id="inc-acceptance"
                  v-model="acceptanceCriteria"
                  v-bind="acceptanceCriteriaProps"
                  class="form-control"
                  rows="3"
                  placeholder="Condições para considerar o incentivo válido."
                />
                <small class="fs-hint">Opcional. Usado para validar antes de conceder o incentivo.</small>
              </div>
            </div>
          </div>

          <div class="fs-section">
            <div class="fs-header">
              <div class="fs-num">3</div>
              <h4 class="fs-title">Banner</h4>
            </div>
            <p class="fs-sub">Imagem exibida para o parceiro quando o incentivo estiver ativo.</p>

            <div class="row g-3">
              <div class="col-12">
                <IncentiveBannerUploader
                  ref="bannerUploaderRef"
                  v-model="bannerFileExternalId"
                />
                <small class="fs-hint">
                  Opcional. O arquivo só é enviado ao clicar em "Salvar incentivo".
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<style scoped lang="scss">
.fs-section {
  padding: 24px;
  border-bottom: 1px solid #e4e6eb;

  &:last-of-type {
    border-bottom: none;
  }
}

.fs-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}

.fs-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #00b569;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  flex-shrink: 0;
}

.fs-title {
  font-size: 15px;
  font-weight: 700;
  color: #222222;
  margin: 0;
}

.fs-sub {
  font-size: 12.5px;
  color: #6b7280;
  margin-left: 36px;
  margin-bottom: 16px;
  line-height: 1.4;
}

.fs-label {
  font-size: 12px;
  font-weight: 600;
  color: #222222;
  margin-bottom: 4px;
  display: block;
}

.fs-req {
  color: #ef4444;
}

.fs-hint {
  font-size: 11.5px;
  color: #6b7280;
  line-height: 1.4;
  display: block;
  margin-top: 4px;
}

.form-control {
  padding: 9px 12px;
  border-color: #e4e6eb;
  border-radius: 6px;
  font-size: 13px;

  &:focus {
    border-color: #00b569;
    box-shadow: 0 0 0 3px rgba(0, 181, 105, 0.1);
  }

  &:disabled {
    background: #f5f6f8;
    color: #9ca3af;
  }
}
</style>
